import { Injectable } from '@angular/core';
import { CellData, TabData, TableData } from '@ddt-web/data-service';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

@Injectable()
export class HypotheticalGrowthValueService {

	public static prepareAccumulativeReturns(tabDataWithPeriodReturns: TabData, startDate: dayjs.Dayjs): TabData {
		const compoundSum = ((sum: number) => (cells: CellData[], index: number) => {
			// we need to keep the startPoint cell as it is (with the starting default hypo value and 100% return)
			const isStartPointCell = index === 0;

			if (!isStartPointCell && cells[1].rawValue) {
				const currentReturn = +cells[1].rawValue;
				if (index === 1) { // first value to be updated (as index 0 should always stay the same)
					sum = currentReturn;
				} else {
					sum = sum + currentReturn + (sum * currentReturn);
				}
			}
			return [cells[0], { ...cells[1], rawValue: sum + 1 }];
		});

		return TabData.create({
			...tabDataWithPeriodReturns,
			tableData: tabDataWithPeriodReturns.tableData.map((data: TableData) =>
				TableData.create({
					...data,
					cells: data.cells
						.filter(cell => dayjs.utc(`${cell[0].rawValue}`, 'YYYY-MM-DD') >= startDate)
						.map(compoundSum(0))
				})
			),
			error: tabDataWithPeriodReturns.error
		});
	}

	constructor(private applicationStateProxyService: ApplicationStateStoreProxyService) { }

	/**
	* @description validateHypoAmount: handles validation of the input (numbers and single decimal points).
	*/
	public validateHypoAmount(value: string): { isInvalidInput: boolean; hypotheticalAmountNum: number; hypotheticalAmountTxt: string } {
		let hypotheticalAmountTxt = this.extractCommas(value);
		let isInvalidInput: boolean;
		let hypotheticalAmountNum: number;
		if (this.isValidInput(hypotheticalAmountTxt)) {
			const formattedNumber = parseFloat(hypotheticalAmountTxt);

			if (isNaN(formattedNumber) || hypotheticalAmountTxt === '0') {
				isInvalidInput = true;
			} else {
				isInvalidInput = false;
				hypotheticalAmountNum = formattedNumber;
				hypotheticalAmountTxt = formattedNumber.toLocaleString(undefined, { maximumFractionDigits: 2 });
			}
		} else {
			isInvalidInput = true;
		}

		return { isInvalidInput, hypotheticalAmountNum, hypotheticalAmountTxt };
	}

	private extractCommas(value: string): string {
		return value?.replace(/,/g, '');
	}

	/**
	 * @description updateHypotheticalGrowth: updates the multiplier value for the hypothetical grow chart data.
	*/
	public updateHypotheticalGrowth(hypotheticalAmountNum: number): void {
		this.applicationStateProxyService.updateHypotheticalGrowthMultiplier({
			hypotheticalGrowthMultiplier: hypotheticalAmountNum
		});
	}

	/**
	 * @description updateHypotheticalGrowth: updates the start timestamp for the hypothetical grow chart data.
	*/
	public updateHypotheticalGrowthStart(hypotheticalGrowthStartTimestamp: number): void {
		this.applicationStateProxyService.updateHypotheticalGrowthStart({
			hypotheticalGrowthStartTimestamp
		});
	}

	private isValidInput(value: string): boolean {
		const numberWithOptionalDecimalRegex = /^$|^((\d*\.)?|\d)+$/;
		return numberWithOptionalDecimalRegex.test(value);
	}
}
